import React, { FC } from 'react';
import { Space, Typography, Tag, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ReferenceField, BooleanField, TextField, Drawer } from '~/components';
import { employees } from '~/routes';
import { Employee, Terminal, Location } from '~/types';
import { getTerminalById } from '~/api/terminals';
import EmployeeEdit from './EmployeeEdit';
import EmployeeGetAccessCode from './EmployeeGetAccessCode';
import EmployeeDelete from './EmployeeDelete';
import { usePageShow } from '~/hooks/usePageQuery';
import { getEmployeeById } from '~/api/employees';
import { getEmployeeRole } from '~/utils/getEmployeeRole';
import { useEmployeesRoles } from '~/hooks/employees';
import { getRoleColor } from '~/utils/employees';
import { getLocationById } from '~/api/locations';
import { isEmpty } from 'lodash';
import { useIsSelf } from '~/hooks/identity';

const EmployeeShow: FC<any> = (props) => {
  const { className } = props;

  const { data: employee, open, showClose } = usePageShow<Employee>(employees);
  const { data: employeeRoles } = useEmployeesRoles();

  const [t] = useTranslation('employees');
  const dt = (key: string) => t(`translation:${key}`);

  const { firstName, lastName, email } = employee || {};
  const isSelf = useIsSelf(email);
  const selectedCashierOrAgent =
    employee?.roles?.includes('BUSINESS_CASHIER') ||
    employee?.roles?.includes('BUSINESS_SALES_AGENT');
  const selectedManager = employee?.roles?.includes('BUSINESS_MANAGER');
  if (!employee) return null;
  return (
    <Drawer
      className={className}
      title={`${firstName || ''} ${lastName || ''} ${isSelf ? `(${dt('you')})` : ''}`}
      onClose={showClose}
      visible={open}
      footer={
        <Row gutter={[8, 8]} justify="end" className="EmployeeShow-BottomTooltip">
          {!isSelf && (
            <Col>
              <EmployeeDelete
                id={employee?.id!}
                closeParent={showClose}
                hasAffiliation={employee?.references && employee?.references.length > 0}
                name={`${firstName} ${lastName}`}
              />
            </Col>
          )}
          {selectedCashierOrAgent && (
            <Col>
              <EmployeeGetAccessCode record={employee} />
            </Col>
          )}
          <Col>
            <EmployeeEdit initialValues={employee} />
          </Col>
        </Row>
      }
    >
      <Space direction="vertical" size="large" style={fullWidth}>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <TextField label={t('email')}>{employee?.email}</TextField>
          <TextField label={t('phoneNumber')}>
            <bdi>{employee?.phoneNumber}</bdi>
          </TextField>
          <TextField label={t('documentType')}>{employee?.document?.type}</TextField>
          <TextField label={t('documentNumber')}>{employee?.document?.number}</TextField>
        </Space>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <Typography.Title className="EmployeeShow-Title" level={5}>
            {t('authority')}
          </Typography.Title>
          <TextField label={t('roles')}>
            {employee?.roles?.map((item) => (
              <Tag key={item} color={getRoleColor(item)}>
                {getEmployeeRole(item, employeeRoles)}
              </Tag>
            ))}
          </TextField>
          {selectedCashierOrAgent && (
            <TextField label={t('terminal')}>
              {employee?.terminalId && (
                <ReferenceField
                  selector={(terminal: Terminal) => terminal.label}
                  query={getTerminalById(employee.terminalId)}
                  deps={[employee.terminalId]}
                />
              )}
            </TextField>
          )}
          {selectedCashierOrAgent && (
            <TextField label={t('cashierAgentAt')}>
              {employee?.workLocationId ? (
                <ReferenceField
                  selector={(location: Location) => location.name}
                  query={getLocationById(employee?.workLocationId)}
                  deps={[employee.workLocationId]}
                />
              ) : undefined}
            </TextField>
          )}
          {selectedManager && (
            <TextField label={t('managesLocations')}>
              {employee?.managedLocationIds && !isEmpty(employee?.managedLocationIds)
                ? employee?.managedLocationIds.map((item) => (
                    <ReferenceField
                      selector={(location: Location) => location.name}
                      query={getLocationById(item)}
                      deps={[item]}
                    />
                  ))
                : undefined}
            </TextField>
          )}
          <TextField label={t('position')}>{employee?.authority?.position}</TextField>
          <TextField label={t('reportsTo')}>
            {employee?.reportsTo && (
              <ReferenceField
                selector={(employee: Employee) => `${employee.firstName} ${employee.lastName}`}
                query={getEmployeeById(employee.reportsTo)}
                deps={[employee.reportsTo]}
              />
            )}
          </TextField>
          <Space direction="horizontal" size="large">
            <BooleanField label={t('owner')}>{employee?.authority?.owner}</BooleanField>
            <BooleanField label={t('executive')}>{employee?.authority?.executive}</BooleanField>
          </Space>
        </Space>
      </Space>
    </Drawer>
  );
};

const fullWidth = { width: '100%' };

export default styled(EmployeeShow)`
  .EmployeeShow-BottomTooltip {
    margin-bottom: 0 !important;
  }

  .EmployeeShow-Title {
    margin-bottom: 0;
  }
`;
